import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CancelToken } from 'axios';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import Card from '../../../../components/Card';
import { AppUsageEvolutionDTO } from '../../../../models/dto/AppUsageEvolutionDTO';
import { ChartData } from '../../../../models/dto/ExpectedRealizedDTO';
import { BaseEnum, getEnumLabel } from '../../../../models/enum/BaseEnum';
import APP_USAGE_EVOLUTION_ENUM, {
  FUNCIONARIOS,
} from '../../../../models/enum/AppUsageEvolutionEnum';
import { getCancelTokenSource, isCancel } from '../../../../services/api';
import {
  downloadAppUsageEvolution,
  findAllBranches,
  findAppUsageEvolution,
} from '../../../../services/personnelDepartment.service';
import { isEmpty } from '../../../../utils/validation.util';
import { format, toDate } from '../../../../utils/date.util';
import FileDownloader from '../../../../components/FileDownloader';
import CustomDialog from '../../../../components/CustomDialog';
import { BranchDTO } from '../../../../models/dto/BranchDTO';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    divider: {
      height: 40,
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: window.innerHeight * 0.23,
    },
    contentContainer: {
      paddingTop: 0,
    },
  }),
);

const AppUsageEvolutionCard: React.FC = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [chartSelected, setChartSelected] = useState<string>(
    FUNCIONARIOS.value,
  );
  const [branches, setBraches] = useState<BranchDTO[]>([]);
  const [branchIdSelected, setBranchIdSelected] = useState('');

  const twelveMonthsPeriod: { start: string; end: string } = useMemo(() => {
    return {
      start: moment().startOf('year').format('YYYY-MM-DD'),
      end: moment().endOf('year').format('YYYY-MM-DD'),
    };
  }, []);

  const buildDataSet = useCallback((data: AppUsageEvolutionDTO[]) => {
    const builtChartData: ChartData = {
      labels: data.map(item =>
        format(toDate(item.anoPeriodo) ?? new Date(), 'MMM/YYYY'),
      ),
      datasets: [
        {
          label: 'Quantidade de acessos',
          fill: false,
          backgroundColor: 'rgba(138, 178, 236, 1)',
          hoverBackgroundColor: 'rgba(138, 178, 236, 1)',
          lineTension: 0,
          data: data.map(item => item.quantidadeUso),
        },
      ],
    };
    setChartData(builtChartData);
  }, []);

  const findAppUsageChartData = useCallback(
    async (type: string, cancelToken?: CancelToken) => {
      try {
        setLoading(true);
        const currentYear = moment().year();
        if (!isEmpty(currentYear)) {
          const { data } = await findAppUsageEvolution(
            type.toLowerCase(),
            String(currentYear),
            cancelToken,
          );
          buildDataSet(data);
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [buildDataSet],
  );

  const findBranches = useCallback(
    async (cancelToken?: CancelToken) => {
      try {
        setLoadingBranches(loadingBranches);
        const { data } = await findAllBranches(cancelToken);
        setBraches(data);
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      } finally {
        setLoading(loadingBranches);
      }
    },
    [loadingBranches],
  );

  const handleChangeChartSelect = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const typeSelected = event.target.value as string;
      if (typeSelected) {
        setChartSelected(typeSelected);
        findAppUsageChartData(typeSelected);
      }
    },
    [findAppUsageChartData],
  );

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();
    findAppUsageChartData(FUNCIONARIOS.value, cancelTokenSource.token);
    findBranches(cancelTokenSource.token);
  }, [findAppUsageChartData, findBranches]);

  const systemAdherenceChart = (
    <Line data={chartData || {}} height={window.innerHeight * 0.165} />
  );

  return (
    <Card className={classes.card}>
      <Grid
        container
        alignItems="center"
        justify="space-between"
        className={classes.cardHeaderContainer}
      >
        <Grid item>
          <Typography color="textSecondary" variant="h5">
            Evolução do uso do Aplicativo
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => setOpenDetailDialog(true)}
          >
            Ver Detalhes
          </Button>
        </Grid>
        <Grid container item>
          <Select
            disabled={loading}
            value={chartSelected}
            disableUnderline
            onChange={handleChangeChartSelect}
          >
            {loading ? (
              <MenuItem disabled value="" selected>
                Carregando...
              </MenuItem>
            ) : (
              APP_USAGE_EVOLUTION_ENUM.map((item: BaseEnum) => (
                <MenuItem key={item.numberValue} value={item.value}>
                  {item.label}
                </MenuItem>
              ))
            )}
          </Select>
        </Grid>
      </Grid>

      <CardContent className={classes.contentContainer}>
        {!loading ? (
          <>{systemAdherenceChart}</>
        ) : (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </CardContent>

      {openDetailDialog ? (
        <CustomDialog
          open={openDetailDialog}
          handleClose={() => setOpenDetailDialog(false)}
          hideDefaultButtons
          showCloseButton
          title={`Gerar CSV com dados sobre a utilização do APP pelos ${getEnumLabel(
            chartSelected,
            APP_USAGE_EVOLUTION_ENUM,
          )}`}
          maxWidth="md"
        >
          <Grid container justify="center" style={{ paddingBottom: '32px' }}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="select-type-label">
                Selecione a Regional
              </InputLabel>
              <Select
                disabled={loading}
                disableUnderline
                value={branchIdSelected}
                variant="outlined"
                style={{ minWidth: '315px' }}
                label="Selecione a Regional"
                onChange={event =>
                  setBranchIdSelected(event.target.value as any)
                }
              >
                <MenuItem value="">Selecione uma Regional</MenuItem>
                {branches.map((item: BranchDTO) => (
                  <MenuItem key={item.idRegional} value={item.idRegional}>
                    {item.nomeRegional}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FileDownloader
              disabled={!branchIdSelected || loadingBranches}
              onSelect={downloadAppUsageEvolution}
              onSelectParams={{
                type: chartSelected.toLowerCase(),
                branchId: branchIdSelected,
                startDate: twelveMonthsPeriod.start,
                endDate: twelveMonthsPeriod.end,
              }}
              icon="download"
              typeExpected="csv"
              color="primary"
            />
          </Grid>
        </CustomDialog>
      ) : null}
    </Card>
  );
};

export default AppUsageEvolutionCard;
